/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { patientApi } from '../lib/api';
import { usePatientAuth } from './PatientAuthContext';
import { extractData } from '../hooks/useQueries';

export interface LinkedClinic {
  clinicId: string;
  clinicName?: string;
  clinicLogoUrl?: string;
  isPrimary: boolean;
}

const ACTIVE_CLINIC_KEY = 'active_patient_clinic_id';

interface PatientTenantContextType {
  linkedClinics: LinkedClinic[];
  /** The clinic currently being viewed — always resolved, defaults to primary. */
  activeClinic: LinkedClinic | null;
  isMultiClinic: boolean;
  loading: boolean;
  /** Switches the active clinic and reloads — simplest way to guarantee every screen re-scopes correctly. */
  switchClinic: (clinicId: string) => void;
}

const PatientTenantContext = createContext<PatientTenantContextType>({
  linkedClinics: [],
  activeClinic: null,
  isMultiClinic: false,
  loading: true,
  switchClinic: () => {},
});

export function PatientTenantProvider({ children }: { children: React.ReactNode }) {
  const { patient } = usePatientAuth();
  const [linkedClinics, setLinkedClinics] = useState<LinkedClinic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!patient) {
      setLinkedClinics([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    patientApi.clinics.getLinked()
      .then((res) => setLinkedClinics(extractData<LinkedClinic[]>(res ?? {}) ?? []))
      .catch(() => setLinkedClinics([]))
      .finally(() => setLoading(false));
  }, [patient?.id]);

  const overrideId = sessionStorage.getItem(ACTIVE_CLINIC_KEY);
  const activeClinic =
    linkedClinics.find((c) => c.clinicId === overrideId) ??
    linkedClinics.find((c) => c.isPrimary) ??
    linkedClinics[0] ??
    null;

  const switchClinic = useCallback((clinicId: string) => {
    const target = linkedClinics.find((c) => c.clinicId === clinicId);
    if (!target) return;
    if (target.isPrimary) {
      sessionStorage.removeItem(ACTIVE_CLINIC_KEY);
    } else {
      sessionStorage.setItem(ACTIVE_CLINIC_KEY, clinicId);
    }
    // Reload so every screen's already-in-flight queries re-fetch under the new clinic scope,
    // rather than trying to invalidate a dozen separate query keys individually.
    window.location.href = '/patient/dashboard';
  }, [linkedClinics]);

  return (
    <PatientTenantContext.Provider value={{
      linkedClinics, activeClinic, isMultiClinic: linkedClinics.length > 1, loading, switchClinic,
    }}>
      {children}
    </PatientTenantContext.Provider>
  );
}

export function usePatientTenant() {
  return useContext(PatientTenantContext);
}
